import React from 'react';
import { Link } from 'react-router-dom';
import { statePrivacyLaws } from '../../data/statePrivacyLaws';
const CONTACT_EMAIL = 'contact@findonlinecollege.com';
const commonRights = [{
  name: 'Right to confirm and access',
  text: 'Confirm whether we process your personal data and obtain access to it.'
}, {
  name: 'Right to correct',
  text: 'Correct inaccuracies in your personal data, taking into account the nature of the data and the purposes of processing.'
}, {
  name: 'Right to delete',
  text: 'Delete personal data you provided to us or that we obtained about you, subject to legal exceptions.'
}, {
  name: 'Right to data portability',
  text: 'Obtain a copy of your personal data in a portable and, where technically feasible, readily usable format.'
}, {
  name: 'Right to opt out of targeted advertising',
  text: 'Opt out of the processing of your personal data for targeted advertising.'
}, {
  name: 'Right to opt out of the sale of personal data',
  text: 'Opt out of the sale of your personal data. Sharing your information with partner schools or advertising partners may be considered a "sale" under some state laws.'
}, {
  name: 'Right to opt out of profiling',
  text: 'Opt out of profiling in furtherance of decisions that produce legal or similarly significant effects. We do not engage in this type of profiling.'
}, {
  name: 'Right to appeal',
  text: 'Appeal our decision if we decline to take action on your request.'
}, {
  name: 'Right to non-discrimination',
  text: 'We will not deny services, charge different prices, or provide a different quality of service because you exercised any of these rights.'
}];

/**
 * State consumer data privacy disclosures for residents of every state with a
 * comprehensive privacy law other than California (covered in its own
 * section). Rendered inside the Privacy Policy's numbered section chrome.
 */
export function StatePrivacyRights() {
  return <>
      <p className="text-gray-700 leading-relaxed" data-id="element-632" data-mp="3nfkm9frhcchg">
        A growing number of U.S. states give their residents rights over their
        personal data. This section describes those rights and how to exercise
        them. To keep things simple and fair,{' '}
        <strong className="text-navy" data-id="element-633" data-mp="3s7u9up54h0gk">
          we extend the rights described below to residents of every state
          listed in the table, whether or not your state's law specifically
          requires each one.
        </strong>
      </p>

      <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-634" data-mp="2zkhkk77kuejk">
        States Covered by This Section
      </h3>
      <div className="overflow-x-auto rounded-xl border border-gray-200" data-id="element-635" data-mp="3djzgr7n7cu2y">
        <table className="w-full min-w-[560px] text-left text-sm" data-id="element-636" data-mp="38itkx0y8wqp7">
          <caption className="sr-only" data-id="element-637" data-mp="ss74vqmxiew1">
            U.S. state privacy laws and the rights specific to each state
          </caption>
          <thead className="bg-gray-50 text-navy" data-id="element-638" data-mp="nskppbnjiokl">
            <tr data-id="element-639" data-mp="waeaqquhjfdb">
              <th scope="col" className="px-4 py-3 font-semibold" data-id="element-640" data-mp="w9v260qq63by">
                State
              </th>
              <th scope="col" className="px-4 py-3 font-semibold" data-id="element-641" data-mp="10c86qltqjv75">
                Law and effective date
              </th>
              <th scope="col" className="px-4 py-3 font-semibold" data-id="element-642" data-mp="mdrqa362mqbt">
                Additional state-specific rights
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-643" data-mp="1f0m80sbi7bll">
            {statePrivacyLaws.map(entry => <tr key={entry.state} className="align-top" data-id="element-644" data-mp="1k08m13z5ovvg">
                <th scope="row" className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap" data-id="element-645" data-mp="1k1s1wtgwh4wp">
                  {entry.state}
                </th>
                <td className="px-4 py-3 text-gray-700" data-id="element-646" data-mp="1xzqh2131p5d9">
                  {entry.law}
                  <span className="block text-xs text-gray-500 mt-0.5" data-id="element-647" data-mp="1xxx4y69nxsxv">
                    Effective {entry.effective}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-700" data-id="element-648" data-mp="e6741gfbqj2v">
                  {entry.notes || '—'}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-gray-500" data-id="element-649" data-mp="54qs348v28wj">
        California residents: see Section 10 for your rights under the CCPA and
        CPRA.
      </p>

      <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-650" data-mp="xsmxqygerb5g">Your Rights</h3>
      <ul className="list-disc pl-6 space-y-2 marker:text-crimson" data-id="element-651" data-mp="xupmlf5tqy8l">
        {commonRights.map(right => <li key={right.name} className="text-gray-700 leading-relaxed" data-id="element-652" data-mp="svw75h6z8x5s">
            <strong className="text-navy" data-id="element-653" data-mp="o18t6c9lnl5g">{right.name}:</strong> {right.text}
          </li>)}
      </ul>

      <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-654" data-mp="j1mfk1pv02w4">
        Personal Data We Process and Why
      </h3>
      <p className="text-gray-700 leading-relaxed" data-id="element-655" data-mp="1gkxmloe898kk">
        We process identifiers (name, email address, phone number, postal
        address, IP address), education and program-interest information
        (degree level, area of study, graduation year, highest education
        completed), and internet activity (pages visited, device and browser
        information). We use this data to match you with educational programs,
        connect you with partner schools you request information from, send
        communications you consent to, measure and improve our services, and
        deliver advertising. Section 4 lists the categories of third parties we
        share personal data with: educational institutions, advertising
        partners, and service providers.
      </p>

      <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-656" data-mp="2buay1kk9wyyi">
        Sale of Personal Data and Targeted Advertising
      </h3>
      <p className="text-gray-700 leading-relaxed" data-id="element-657" data-mp="2lqdvid46k9f4">
        We do not sell personal data for money. However, when you ask to be
        matched with schools, we disclose your information to partner
        educational institutions, and we use cookies and similar technologies
        from advertising partners to deliver targeted advertising. Some state
        laws treat these activities as a "sale" of personal data or as
        "targeted advertising." You may opt out of both at any time using the
        methods below.
      </p>

      <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-658" data-mp="unrohfpmhkrc">
        Sensitive Data and Minors
      </h3>
      <p className="text-gray-700 leading-relaxed" data-id="element-659" data-mp="19cfi3gfwrken">
        We do not collect or process sensitive data as defined by state privacy
        laws (such as precise geolocation, health, biometric, or financial
        account data). If that changes, we will obtain your consent first where
        required. We do not knowingly process the personal data of anyone under
        18, and we do not sell the data of, or target advertising to, consumers
        we know are under 18.
      </p>

      <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-660" data-mp="3g588fynyu0tm">
        How to Exercise Your Rights
      </h3>
      <p className="text-gray-700 leading-relaxed" data-id="element-661" data-mp="361jcvjymuv0s">
        You can submit a request using any of these methods:
      </p>
      <ol className="list-decimal pl-6 space-y-3 marker:text-crimson marker:font-semibold" data-id="element-662" data-mp="3a11wmcepx021">
        <li className="text-gray-700 leading-relaxed" data-id="element-663" data-mp="2w9x37s1y1405">
          <strong className="text-navy" data-id="element-664" data-mp="318zxqikarrtv">Online form:</strong> complete the
          request form on our{' '}
          <Link to="/do-not-sell-my-info" className="text-navy underline hover:text-crimson" data-id="element-665" data-mp="2mbzgj8zqff9n">
            Your Privacy Choices
          </Link>{' '}
          page and select the type of request.
        </li>
        <li className="text-gray-700 leading-relaxed" data-id="element-666" data-mp="2ddnjao4d1j88">
          <strong className="text-navy" data-id="element-667" data-mp="2i9vvptr6aybp">Email:</strong> write to{' '}
          <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Privacy Rights Request')}`} className="text-navy underline hover:text-crimson" data-id="element-668" data-mp="bk7qbkksvsa3">
            {CONTACT_EMAIL}
          </a>{' '}
          with the subject line{' '}
          <strong className="text-navy" data-id="element-669" data-mp="1p5u9evs5jtvm">"Privacy Rights Request"</strong>, your
          state of residence, and the right you want to exercise.
        </li>
        <li className="text-gray-700 leading-relaxed" data-id="element-670" data-mp="1fd53shja9a87">
          <strong className="text-navy" data-id="element-671" data-mp="1fbk64j1o9b64">Browser signal:</strong> enable a
          universal opt-out preference signal such as{' '}
          <a href="https://globalprivacycontrol.org/" target="_blank" rel="noopener noreferrer" className="text-navy underline hover:text-crimson" data-id="element-672" data-mp="16drrlh3ry7js">
            Global Privacy Control
          </a>{' '}
          in your browser. We treat it as a valid request to opt out of the
          sale of your personal data and targeted advertising for that browser.
        </li>
        <li className="text-gray-700 leading-relaxed" data-id="element-673" data-mp="2qrs92ll0moib">
          <strong className="text-navy" data-id="element-674" data-mp="2mrroej7bph2t">Marketing messages:</strong> click
          "unsubscribe" in any marketing email or reply STOP to any text
          message.
        </li>
      </ol>

      <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-675" data-mp="ditvvjqk9pc1">
        What Happens After You Submit a Request
      </h3>
      <ul className="list-disc pl-6 space-y-2 marker:text-crimson" data-id="element-676" data-mp="i90kvkozjbut">
        <li className="text-gray-700 leading-relaxed" data-id="element-677" data-mp="ibwif7wx7yiq">
          <strong className="text-navy" data-id="element-678" data-mp="m9u7maqoykm4">Verification:</strong> for access,
          portability, correction, and deletion requests, we will ask you to
          confirm information we already hold (such as your email address and
          phone number) so we can verify your identity. Opt-out requests do not
          require verification.
        </li>
        <li className="text-gray-700 leading-relaxed" data-id="element-679" data-mp="3kmex0w9v7a1">
          <strong className="text-navy" data-id="element-680" data-mp="3m7b0uy8iya0">Timing:</strong> we respond within 45
          days of receiving your request. If we need more time, we may extend
          this once by up to 45 additional days where reasonably necessary, and
          we will tell you why within the first 45 days. Opt-out requests are
          honored as soon as practicable and no later than 15 business days.
        </li>
        <li className="text-gray-700 leading-relaxed" data-id="element-681" data-mp="89oagscmn2fd">
          <strong className="text-navy" data-id="element-682" data-mp="d1fvyogxehyn">Cost:</strong> requests are free. You
          may submit at least two requests in any 12-month period at no charge;
          we may decline or charge a reasonable fee only for requests that are
          manifestly unfounded, excessive, or repetitive.
        </li>
        <li className="text-gray-700 leading-relaxed" data-id="element-683" data-mp="3lgzk592qpa6u">
          <strong className="text-navy" data-id="element-684" data-mp="3ljvkf4228ayg">Authorized agents:</strong> where your
          state allows, an authorized agent may submit a request on your
          behalf. We may require signed written permission and ask you to verify
          your identity directly with us. Parents or legal guardians may submit
          requests on behalf of a minor.
        </li>
      </ul>

      <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-685" data-mp="19qnhvxlgit33">
        How to Appeal Our Decision
      </h3>
      <p className="text-gray-700 leading-relaxed" data-id="element-686" data-mp="2u9hh11d1kxg6">
        If we decline to take action on your request, we will explain why. You
        may appeal that decision by replying to our response or by emailing{' '}
        <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Privacy Request Appeal')}`} className="text-navy underline hover:text-crimson" data-id="element-687" data-mp="2lfut1xf2syhj">
          {CONTACT_EMAIL}
        </a>{' '}
        with the subject line{' '}
        <strong className="text-navy" data-id="element-688" data-mp="26jna10fem33u">"Privacy Request Appeal"</strong> within a
        reasonable time after receiving our decision. We will respond to your
        appeal in writing within the time your state's law requires (generally
        45 to 60 days), including an explanation of any action taken or not
        taken. If we deny your appeal, you may contact your state's Attorney
        General to submit a complaint, and we will tell you how to do so in our
        response.
      </p>
    </>;
}